import React from "react";
import { Button } from "@/components/ui/button";

interface NavigationButtonsProps {
  activeStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  isValid?: boolean;
  isFormComplete?: boolean;
  isCoverPage?: boolean;
}

export function NavigationButtons({
  activeStep,
  totalSteps,
  onNext,
  onPrevious,
  onSubmit,
  isValid = true,
  isFormComplete = false,
  isCoverPage = false,
}: NavigationButtonsProps) {
  const isLastStep = activeStep === totalSteps - 1;
  const isFirstStep = activeStep === 0;

  // Estes botoes ficam DENTRO do card, que redefine --background para escuro
  // (ver FormStepLayout). Como eles usam `text-background` sobre fundo azul,
  // aqui o branco e o correto -- restauramos o token so no proprio botao.
  const textoBrancoNoBotao = { '--background': '0 0% 100%' } as React.CSSProperties;

  // Se estiver na página de capa, mostrar apenas o botão "Preencher Formulário"
  if (isCoverPage) {
    return (
      <>
        {/* Desktop button */}
        <div className="hidden sm:flex justify-center mt-8">
          <Button
            type="button"
            style={textoBrancoNoBotao}
            className="rounded-md h-12 px-8 bg-primary/90 hover:bg-primary text-background font-medium"
            onClick={onNext}
          >
            Preencher Formulário
          </Button>
        </div>

        <div className="flex justify-between">&nbsp;</div>

        {/* Mobile button */}
        <footer className="w-full sm:hidden flex z-10 gap-4 justify-center items-center">
          <Button
            variant="default"
            style={textoBrancoNoBotao}
            className="flex-1 rounded-md h-12 px-8 bg-primary/90 hover:bg-primary text-background font-medium"
            onClick={onNext}
          >
            Preencher Formulário
          </Button>
        </footer>
      </>
    );
  }

  // Determina qual função usar para o botão de ação
  const handleAction = isLastStep ? onSubmit : onNext;
  const buttonText = isLastStep ? "Finalizar" : "Próximo";
  const buttonStyle = isLastStep
    ? "bg-primary/90 hover:bg-primary text-background font-medium"
    : "";

  // No último passo, verificamos se o formulário está completo
  const isButtonDisabled = isLastStep 
    ? (!isValid || !isFormComplete) // No último passo, desabilitar se não for válido ou formulário incompleto
    : !isValid; // Nos outros passos, desabilitar apenas se o campo atual não for válido

  return (
    <>
      {/* Desktop buttons */}
      <div className="hidden sm:flex justify-between mt-8">
        <Button
          disabled={isFirstStep}
          variant="ghost"
          className="rounded-md h-9 px-2 bg-grey-light text-grey-dark"
          onClick={onPrevious}
        >
          Anterior
        </Button>

        <Button
          type="button"
          disabled={isButtonDisabled}
          style={isLastStep ? textoBrancoNoBotao : undefined}
          className={`rounded-md h-9 px-4 ${buttonStyle}`}
          onClick={handleAction}
        >
          {buttonText}
        </Button>
      </div>

      <div className="flex justify-between">&nbsp;</div>

      {/* Mobile footer */}
      <footer className="w-full sm:hidden flex z-10 gap-2 justify-between items-center">
        <Button
          variant={isFirstStep ? "ghost" : "default"}
          className={`flex-1 rounded-md h-9 px-1 text-sm ${
            isFirstStep ? "bg-grey-light text-grey-dark" : ""
          }`}
          onClick={onPrevious}
          disabled={isFirstStep}
        >
          Anterior
        </Button>
        <Button
          variant="ghost"
          className="flex-1 bg-background/10 hover:bg-background/10 cursor-default rounded-md hover:text-grey-light text-grey-light h-9 px-1 text-xs"
        >
          {activeStep + 1}/{totalSteps}
        </Button>
        <Button
          variant={isLastStep ? "ghost" : "default"}
          disabled={isButtonDisabled}
          style={isLastStep ? textoBrancoNoBotao : undefined}
          className={`flex-1 rounded-md h-9 px-1 text-sm ${buttonStyle}`}
          onClick={handleAction}
        >
          {buttonText}
        </Button>
      </footer>
    </>
  );
}
